/**
 * Bottom Navigation Component
 * Glassmorphism style navigation bar with FAB for session recording
 * 
 * Features:
 * - Fixed bottom with glass effect (blur + transparency)
 * - 3 navigation items: Home, Read, Profile
 * - Floating Action Button for session recording
 * - Active state with primary color
 * - Responsive design
 */

import { Home, BookOpen, User, Plus } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface BottomNavigationProps {
  onSessionClick: () => void;
}

interface NavItem {
  icon: typeof Home;
  label: string;
  path: string;
}

const navItems: NavItem[] = [
  { icon: Home, label: 'หน้าแรก', path: '/dashboard' },
  { icon: BookOpen, label: 'อ่าน', path: '/read' },
  { icon: User, label: 'โปรไฟล์', path: '/profile' },
];

export function BottomNavigation({ onSessionClick }: BottomNavigationProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={cn(
        // Fixed position with spacing from edges
        'fixed bottom-4 left-4 right-4 z-49',
        // Enhanced glassmorphism effect
        'bg-background/60 backdrop-blur-2xl',
        'border border-border/40',
        'shadow-2xl shadow-black/10',
        // Shape
        'rounded-2xl',
        // Layout
        'flex items-center justify-between',
        'px-4 py-3',
        // Animation
        'transition-all duration-300',
        // Subtle glow effect
        'ring-1 ring-white/10'
      )}
      aria-label="เมนูนำทางหลัก"
    >
      {/* Navigation Items (Left Side) */}
      <div className="flex items-center gap-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={cn(
                // Base styles
                'flex flex-col items-center justify-center',
                'px-4 py-2 rounded-xl',
                'transition-all duration-200',
                'cursor-pointer',
                // Active state
                active
                  ? 'text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                // Focus state
                'focus:outline-none  focus:ring-ring focus:ring-offset-2'
              )}
              aria-label={item.label}
              aria-current={active ? 'page' : undefined}
            >
              <Icon 
                className={cn(
                  'w-5 h-5 mb-1',
                  'transition-transform duration-200',
                  active && 'scale-110 text-primary'
                )} 
              />
              {/* <span className="text-xs font-medium">{item.label}</span> */}
            </button>
          );
        })}
      </div>

      {/* Floating Action Button (Right Side) */}
      <button
        onClick={onSessionClick}
        className={cn(
          // FAB styling
          'flex items-center justify-center',
          'w-14 h-14',
          'rounded-full',
          // Theme colors
          'bg-primary text-primary-foreground',
          'shadow-lg shadow-primary/30',
          // Hover effects
          'hover:bg-primary/90 hover:shadow-xl hover:shadow-primary/40',
          'hover:scale-105',
          'active:scale-95',
          // Animation
          'transition-all duration-200',
          // Focus state
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          // Cursor
          'cursor-pointer'
        )}
        aria-label="บันทึกการอ่าน"
      >
        <Plus className="w-7 h-7" strokeWidth={2.5} />
      </button>
    </nav>
  );
}

export default BottomNavigation;
